<template>
  <div class="user-history-tab">
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" small />
      <span class="ml-2 text-muted">Đang tải lịch sử...</span>
    </div>

    <template v-else-if="histories.length">
      <b-table
        :items="histories"
        :fields="fields"
        bordered
        hover
        small
        responsive
        class="mb-0"
      >
        <template #cell(createdAt)="{ value }">
          {{ formatDateTime(value) }}
        </template>

        <template #cell(action)="{ item }">
          <b-badge :variant="actionVariant(item)">{{ actionLabel(item) }}</b-badge>
        </template>

        <template #cell(updatedByName)="{ value }">
          {{ value || '—' }}
        </template>

        <template #cell(changes)="{ item }">
          <div v-if="isCreateAction(item)" class="text-muted small">
            Tạo mới tài khoản
          </div>
          <template v-else>
            <div
              v-for="change in getDiff(item)"
              :key="change.key"
              class="change-row"
            >
              <span class="change-label">{{ change.label }}:</span>
              <span class="change-before">{{ change.before }}</span>
              <i class="fas fa-arrow-right mx-1 text-muted" style="font-size:10px" />
              <span class="change-after text-primary font-weight-bold">{{ change.after }}</span>
            </div>
            <span v-if="getDiff(item).length === 0" class="text-muted small">—</span>
          </template>
        </template>
      </b-table>
    </template>

    <p v-else class="text-muted mb-0 py-3">Chưa có lịch sử.</p>
  </div>
</template>

<script>
import moment from 'moment';
import ApiService from '@/core/services/api.service';

const ACTION_CREATE = 3;
const ACTION_RESTORE_PASSWORD = 6;
const ACTION_TOGGLE_ACTIVE = 7;

const FIELD_LABELS = {
  username: 'Tên tài khoản',
  isActive: 'Trạng thái',
  roleId: 'Quyền',
  actionMode: 'Chế độ hành động',
  failedLoginCount: 'Số lần đăng nhập sai',
};

const ACTION_MODE_LABEL = {
  0: 'Không có hành động',
  1: 'Yêu cầu đổi mật khẩu lần đầu',
  2: 'Yêu cầu đăng nhập lại',
  3: 'Yêu cầu đăng nhập lại và đổi mật khẩu',
};

export default {
  name: 'UserHistoryTab',
  props: {
    userId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      histories: [],
      meta: { roles: {} },
      fields: [
        { key: 'createdAt', label: 'Thời gian', thStyle: { width: '145px' } },
        { key: 'action', label: 'Hành động', thStyle: { width: '175px' } },
        { key: 'updatedByName', label: 'Người thực hiện', thStyle: { width: '160px' } },
        { key: 'changes', label: 'Thay đổi' },
      ],
    };
  },
  mounted() {
    this.fetchHistories();
  },
  methods: {
    async fetchHistories() {
      this.loading = true;
      try {
        const { data } = await ApiService.get(`v2/users/${this.userId}/histories`);
        if (data.status === 200 || data.status === 1) {
          this.histories = data.data.histories || [];
          this.meta = data.data.meta || { roles: {} };
        }
      } catch {
        // silent
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(val) {
      return val ? moment(val).format('DD/MM/YYYY HH:mm') : '—';
    },
    isCreateAction(item) {
      return item.action === ACTION_CREATE || (item.action == null && item.originalValue == null);
    },
    actionLabel(item) {
      if (item.action === ACTION_RESTORE_PASSWORD) return 'Khôi phục mật khẩu';
      if (item.action === ACTION_TOGGLE_ACTIVE) return 'Khoá / Mở khoá';
      if (this.isCreateAction(item)) return 'Tạo mới';
      return 'Cập nhật';
    },
    actionVariant(item) {
      if (item.action === ACTION_RESTORE_PASSWORD) return 'warning';
      if (item.action === ACTION_TOGGLE_ACTIVE) return 'secondary';
      if (this.isCreateAction(item)) return 'success';
      return 'primary';
    },
    formatValue(key, value) {
      if (value == null || value === '') return '—';
      if (key === 'isActive') return value == 1 ? 'Hoạt động' : 'Đang Khóa';
      if (key === 'roleId') return this.meta.roles[value]?.name || value;
      if (key === 'actionMode') return ACTION_MODE_LABEL[value] ?? String(value);
      return String(value);
    },
    getDiff(item) {
      const orig = item.originalValue || {};
      const next = item.newValue || {};
      const result = [];
      for (const [key, label] of Object.entries(FIELD_LABELS)) {
        const before = orig[key];
        const after = next[key];
        if (String(before ?? '') === String(after ?? '')) continue;
        result.push({
          key,
          label,
          before: this.formatValue(key, before),
          after: this.formatValue(key, after),
        });
      }
      return result;
    },
  },
};
</script>

<style scoped>
.change-row {
  line-height: 1.8;
  font-size: 0.85rem;
}
.change-label {
  color: #6c757d;
  margin-right: 4px;
}
.change-before {
  text-decoration: line-through;
  color: #999;
}
</style>
